/**
 * React Query hooks for the MVB candidate-selection admin surface
 * (P112). Mirrors `twin/api/ibs_admission_hooks.ts`.
 *
 * Backend: twin/mvb_candidate_router.py at /v1/admin/twin/mvb.
 *   GET   /candidates                      -> register
 *   GET   /candidates/{id}                 -> detail + active pack + drift
 *   POST  /derive                          -> batch intake + score
 *   POST  /candidates/{id}/score           -> re-score (signal)
 *   PATCH /candidates/{id}/scores          -> manual override
 *   POST  /packs/{id}/challenge            -> second-line challenge
 *   POST  /packs/{id}/signoff              -> CRO / AE sign-off
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { mvbCandidatesKeys } from "shared/api/queryClient";
import type {
  MvbArchitectureReview,
  MvbCandidateDetail,
  MvbCandidateList,
  MvbChallengeIn,
  MvbConvergence,
  MvbDeriveResult,
  MvbManualScoresIn,
  MvbPack,
  MvbSignoffIn,
} from "shared/api/types";

const BASE = "/v1/admin/twin/mvb";

export function useMvbCandidates(): UseQueryResult<MvbCandidateList, ApiError> {
  return useQuery({
    queryKey: mvbCandidatesKeys.list(),
    queryFn: () => api.get<MvbCandidateList>(`${BASE}/candidates`),
  });
}

export function useMvbCandidate(
  candidateId: string | undefined,
  enabled: boolean,
): UseQueryResult<MvbCandidateDetail, ApiError> {
  return useQuery({
    queryKey: candidateId
      ? mvbCandidatesKeys.detail(candidateId)
      : ["mvb-candidates", "detail", "_disabled"],
    queryFn: () => api.get<MvbCandidateDetail>(`${BASE}/candidates/${candidateId}`),
    enabled: enabled && Boolean(candidateId),
  });
}

export function useMvbConvergence(
  enabled: boolean,
): UseQueryResult<MvbConvergence, ApiError> {
  return useQuery({
    queryKey: mvbCandidatesKeys.convergence(),
    queryFn: () => api.get<MvbConvergence>(`${BASE}/convergence`),
    enabled,
  });
}

/** P439: record that convergence was looked at again after the
 *  architecture model changed. */
export function useMarkConvergenceReviewed(): UseMutationResult<
  MvbArchitectureReview | null,
  ApiError,
  void
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<MvbArchitectureReview | null>(`${BASE}/convergence/reviewed`),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: mvbCandidatesKeys.convergence() }),
  });
}

export function useDeriveMvb(): UseMutationResult<MvbDeriveResult, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: () => api.post<MvbDeriveResult>(`${BASE}/derive`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mvbCandidatesKeys.all }),
  });
}

export function useScoreCandidate(): UseMutationResult<
  MvbPack,
  ApiError,
  { candidateId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId }) =>
      api.post<MvbPack>(`${BASE}/candidates/${candidateId}/score`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mvbCandidatesKeys.all }),
  });
}

export function useOverrideScores(): UseMutationResult<
  MvbPack,
  ApiError,
  { candidateId: string; body: MvbManualScoresIn }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ candidateId, body }) =>
      api.patch<MvbPack>(`${BASE}/candidates/${candidateId}/scores`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: mvbCandidatesKeys.all }),
  });
}

export function useChallengePack(): UseMutationResult<
  MvbPack,
  ApiError,
  { packId: string; body: MvbChallengeIn }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ packId, body }) =>
      api.post<MvbPack>(`${BASE}/packs/${packId}/challenge`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: mvbCandidatesKeys.all }),
  });
}

export function useSignoffPack(): UseMutationResult<
  MvbPack,
  ApiError,
  { packId: string; body: MvbSignoffIn }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ packId, body }) =>
      api.post<MvbPack>(`${BASE}/packs/${packId}/signoff`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: mvbCandidatesKeys.all }),
  });
}
